import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import apiClient from '../api/client';
import { useNotification } from '../components/Notification';
import RichTextEditor from '../components/RichTextEditor';
import './AdminHomePageEditor.css';

// Laukas: įkelti nuotrauką iš kompiuterio arba įvesti URL
function ImageUploadField({ value = '', onChange, label, placeholder = '/uploads/...' }) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const notification = useNotification();

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) {
      notification.error('Pasirinkite nuotraukos failą');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const token = localStorage.getItem('token');
      const sessionId = localStorage.getItem('sessionId');
      const baseURL = apiClient.defaults.baseURL || '/api';
      const url = `${baseURL.replace(/\/$/, '')}/upload/image`;
      const headers = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      if (sessionId) headers['x-session-id'] = sessionId;
      const res = await fetch(url, {
        method: 'POST',
        body: formData,
        headers,
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || `Klaida ${res.status}`);
      }
      if (data.imageUrl) {
        onChange(data.imageUrl);
        notification.success('Nuotrauka įkelta');
      }
    } catch (err) {
      notification.error(err.message || 'Nepavyko įkelti nuotraukos');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="section-props-image-upload">
      {label && <label>{label}</label>}
      {value && (
        <div className="section-props-image-preview">
          <img src={value} alt="" />
          <button
            type="button"
            className="section-props-image-remove"
            onClick={() => onChange('')}
            title="Pašalinti"
          >
            ×
          </button>
        </div>
      )}
      <div className="section-props-image-actions">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? 'Įkeliama...' : 'Įkelti nuotrauką'}
        </button>
        <span className="section-props-image-or">arba URL:</span>
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
      </div>
    </div>
  );
}

const BLOCK_TYPES = [
  { type: 'hero', label: 'Hero', icon: '🖼️' },
  { type: 'feature', label: 'Sekcija (tekstas + nuotrauka)', icon: '▦' },
  { type: 'heading', label: 'Antraštė', icon: 'H' },
  { type: 'text', label: 'Tekstas', icon: '¶' },
  { type: 'image', label: 'Nuotrauka', icon: '🖼' },
  { type: 'button', label: 'Mygtukas', icon: '🔘' },
  { type: 'toggle', label: 'Išskleidžiamas blokas (kaip Specifikacija)', icon: '▾' },
  { type: 'elite_section', label: 'Become the elite (tekstas + 3 vaizdai)', icon: '⬡' },
  { type: 'spacer', label: 'Tarpas', icon: '↕' },
  { type: 'products_grid', label: 'Prekių tinklelis', icon: '▦' },
];

const DEFAULT_SETTINGS = {
  hero: { title: '', subtitle: '', backgroundImage: '', backgroundVideo: '', buttonText: '', buttonLink: '', textAlign: 'left', verticalAlign: 'center' },
  feature: { title: '', text: '', imageSrc: '', imageAlt: '', align: 'left' },
  heading: { tag: 'h2', text: '', textAlign: 'left' },
  text: { content: '', textAlign: 'left' },
  image: { src: '', alt: '', link: '', textAlign: 'left' },
  button: { text: '', link: '', textAlign: 'left' },
  toggle: { title: '', content: '', openByDefault: false },
  elite_section: { title: '', text: '', buttonText: '', buttonLink: '', image1: '', image2: '', image3: '' },
  spacer: { height: 40 },
  products_grid: { title: 'Prekės', limit: 8 },
};

function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2);
}

// Over+Above (oaperformance.com) struktūra – ta pati tvarka ir forma kaip pavyzdyje
function getCurrentHomeStructure() {
  const id = () => generateId();
  return [
    // 1. Hero: Built Different + Born from...
    {
      id: id(),
      type: 'hero',
      settings: {
        title: 'Built Different',
        subtitle: 'Born from elite athlete experience',
        backgroundImage: '',
        backgroundVideo: '',
        buttonText: '',
        buttonLink: '',
      },
    },
    // 2. Prestižo ženklai (brand logos)
    {
      id: id(),
      type: 'text',
      settings: { content: 'The Independent · Marie Claire · GQ · Fashion Network' },
    },
    { id: id(), type: 'spacer', settings: { height: 64 } },
    // 3. Shop Now + 3 prekės
    { id: id(), type: 'heading', settings: { tag: 'h2', text: 'Shop Now' } },
    { id: id(), type: 'spacer', settings: { height: 48 } },
    { id: id(), type: 'products_grid', settings: { title: '', limit: 3 } },
    // 4. Trijų feature sekcijų (dvikolonė: tekstas | nuotrauka) – kaip O+A
    {
      id: id(),
      type: 'feature',
      settings: {
        title: 'UnmatchedStability',
        text: 'K-tape inspired skeletal structures within our patented system offer unparalleled support over major muscle groups and joints. Stabilising your body through even the toughest sessions.',
        imageSrc: '',
        imageAlt: 'UnmatchedStability',
        align: 'left',
      },
    },
    {
      id: id(),
      type: 'feature',
      settings: {
        title: 'MovementEfficiency',
        text: 'Sensory nodes interact at key anatomical locations, heightening proprioception and movement awareness. Helping refine each of your movements for maximum efficiency while you train or compete.',
        imageSrc: '',
        imageAlt: 'MovementEfficiency',
        align: 'right',
      },
    },
    {
      id: id(),
      type: 'feature',
      settings: {
        title: 'ReducedFatigue',
        text: 'Body-mapped compression improves blood flow and reduces muscle oscillation, minimising fatigue and energy loss. Maximise your endurance and sustain high level performance session after session.',
        imageSrc: '',
        imageAlt: 'ReducedFatigue',
        align: 'left',
      },
    },
    // 5. Become the elite: antraštė, tekstas, mygtukas, 3 vaizdai vienoje eilėje
    {
      id: id(),
      type: 'elite_section',
      settings: {
        title: 'Become the elite',
        text: 'A sports performance brand at the cross section of sports science and technology, born from elite athlete experience and a passion for innovation.',
        buttonText: 'Discover the Science',
        buttonLink: '/products',
        image1: '',
        image2: '',
        image3: '',
      },
    },
    // 6. Testimonial: citata + 2 prekės
    {
      id: id(),
      type: 'text',
      settings: {
        content: '"I can\'t and I won\'t run without them. I have quite bad calf problems and since wearing them absolutely nothing. I\'m running more mileage in a week than I ever have. Awesome product." — Mark Findlay, Ex GB Sprinter & HYROX Athlete',
      },
    },
    { id: id(), type: 'spacer', settings: { height: 48 } },
    { id: id(), type: 'products_grid', settings: { title: '', limit: 2 } },
    // 7. A newlevel: antraštė, tekstas, mygtukas, vaizdas/video
    { id: id(), type: 'spacer', settings: { height: 80 } },
    { id: id(), type: 'heading', settings: { tag: 'h2', text: 'A newlevel' } },
    {
      id: id(),
      type: 'text',
      settings: {
        content: 'Patented athletic apparel engineered to tap into your sixth sense, supporting your body during exercise to unlock improved control, efficiency, and reductions in fatigue.',
      },
    },
    { id: id(), type: 'button', settings: { text: 'Discover more', link: '/products' } },
    { id: id(), type: 'image', settings: { src: '', alt: 'A newlevel – vaizdas / video', link: '' } },
    { id: id(), type: 'spacer', settings: { height: 48 } },
    { id: id(), type: 'products_grid', settings: { title: 'Shop', limit: 8 } },
  ];
}

const LOCALES = [
  { code: 'lt', label: 'Lietuvių' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
];

function AdminHomePageEditor() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [sections, setSections] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [viewMode, setViewMode] = useState('edit'); // 'edit' | 'preview'
  const [activeLang, setActiveLang] = useState('lt');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-home-page-builder'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/home-page-builder');
      return res.data;
    },
  });

  useEffect(() => {
    if (data?.content?.sections?.length) {
      setSections(data.content.sections);
    } else if (data && !data.content?.sections?.length) {
      setSections([]);
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: async (content) => {
      await apiClient.put('/admin/home-page-builder', { content: { sections: content } });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-home-page-builder']);
      queryClient.invalidateQueries(['home-page-builder-public']);
      queryClient.refetchQueries(['home-page-builder-public']);
      notification.success('Pirmasis puslapis išsaugotas');
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || 'Klaida išsaugant');
    },
  });

  const addBlock = useCallback((type) => {
    const settings = { ...DEFAULT_SETTINGS[type] };
    setSections((prev) => [...prev, { id: generateId(), type, settings }]);
    notification.success(`Blokas „${BLOCK_TYPES.find((b) => b.type === type)?.label}“ pridėtas`);
  }, [notification]);

  const updateSection = useCallback((id, patch) => {
    setSections((prev) =>
      prev.map((s) => {
        if (s.id !== id) return s;
        const next = { ...s };
        if (patch.settings !== undefined) {
          next.settings = { ...s.settings, ...patch.settings };
        }
        if (patch.rowGroup !== undefined) {
          next.rowGroup = patch.rowGroup || null;
        }
        return next;
      })
    );
  }, []);

  const removeSection = useCallback((id) => {
    setSections((prev) => prev.filter((s) => s.id !== id));
    if (selectedId === id) setSelectedId(null);
  }, [selectedId]);

  const moveSection = useCallback((fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= sections.length) return;
    setSections((prev) => {
      const next = [...prev];
      const [removed] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, removed);
      return next;
    });
  }, [sections.length]);

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
    e.target.closest('.editor-section')?.classList.add('dragging');
  };
  const handleDragEnd = (e) => {
    e.target.closest('.editor-section')?.classList.remove('dragging');
    setDraggedIndex(null);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    const fromIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (Number.isNaN(fromIndex) || fromIndex === dropIndex) return;
    moveSection(fromIndex, dropIndex);
  };

  if (isLoading) {
    return <div className="home-page-editor-loading">Kraunama...</div>;
  }

  const previewUrl = typeof window !== 'undefined' ? `${window.location.origin}/` : '/';

  return (
    <div className="home-page-editor">
      <div className="editor-header">
        <h1>Pirmojo puslapio redaktorius</h1>
        <div className="editor-lang-tabs">
          {LOCALES.map(({ code, label }) => (
            <button
              key={code}
              type="button"
              className={`admin-lang-tab ${activeLang === code ? 'active' : ''}`}
              onClick={() => setActiveLang(code)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="editor-header-actions">
          <div className="editor-view-toggle">
            <button
              type="button"
              className={`btn btn-secondary ${viewMode === 'edit' ? 'active' : ''}`}
              onClick={() => setViewMode('edit')}
            >
              Redaguoti
            </button>
            <button
              type="button"
              className={`btn btn-secondary ${viewMode === 'preview' ? 'active' : ''}`}
              onClick={() => setViewMode('preview')}
            >
              Dabartinio puslapio peržiūra
            </button>
          </div>
          <Link to="/" target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
            Atidaryti puslapį naujame lange
          </Link>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => saveMutation.mutate(sections)}
            disabled={saveMutation.isLoading}
          >
            {saveMutation.isLoading ? 'Išsaugoma...' : 'Išsaugoti'}
          </button>
        </div>
      </div>

      {viewMode === 'preview' ? (
        <div className="editor-preview-wrap">
          <p className="editor-preview-hint">
            Žiūrite dabartinį pradinio puslapio turinį, struktūrą ir dizainą (kaip mato lankytojai).
          </p>
          <iframe
            title="Pradinio puslapio peržiūra"
            src={previewUrl}
            className="editor-preview-iframe"
          />
        </div>
      ) : (
      <div className="editor-body">
        <aside className="editor-panel editor-panel-left">
          <h3>Pridėti bloką</h3>
          <div className="block-types">
            {BLOCK_TYPES.map((block) => (
              <button
                key={block.type}
                type="button"
                className="block-type-btn"
                onClick={() => addBlock(block.type)}
              >
                <span className="block-type-icon">{block.icon}</span>
                <span>{block.label}</span>
              </button>
            ))}
          </div>
        </aside>

        <main className="editor-canvas">
          <p className="editor-canvas-hint">
            Vilkkite blokus norima tvarka. Spustelėkite bloką – jo nustatymai išsiskleis po bloku.
          </p>
          {sections.length === 0 ? (
            <div className="editor-empty">
              <div className="editor-empty-current-structure">
                <h3>Over+Above formos struktūra</h3>
                <p>
                  Įkelkite pirmojo puslapio struktūrą tokia pačia forma kaip <a href="https://www.oaperformance.com/?ref=ecommdesign" target="_blank" rel="noopener noreferrer">Over+Above</a>: Built Different hero, Shop Now, UnmatchedStability, MovementEfficiency, ReducedFatigue, Become the elite, atsiliepimas, A newlevel.
                </p>
                <button
                  type="button"
                  className="btn btn-primary editor-load-structure-btn"
                  onClick={() => {
                    setSections(getCurrentHomeStructure());
                    notification.success('Over+Above formos struktūra įkelta. Redaguokite turinį ir išsaugokite.');
                  }}
                >
                  Įkelti Over+Above formos struktūrą
                </button>
                <p className="editor-empty-or">— arba —</p>
                <p className="editor-empty-add">Pridėkite naujų blokų iš kairės panelės. Spustelėkite bloką, kad redaguotumėte jo duomenis po juo.</p>
              </div>
            </div>
          ) : (
            <div className="editor-sections">
              {sections.map((section, index) => (
                <div key={section.id} className="editor-section-wrap">
                  <div
                    className={`editor-section ${selectedId === section.id ? 'selected' : ''} ${draggedIndex === index ? 'dragging' : ''}`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, index)}
                    onClick={() => setSelectedId((prev) => (prev === section.id ? null : section.id))}
                  >
                    <div className="editor-section-handle" title="Vilkti">⋮⋮</div>
                    <div className="editor-section-preview">
                      <SectionPreview section={section} activeLang={activeLang} />
                    </div>
                    <span className="editor-section-toggle" title={selectedId === section.id ? 'Suskleisti nustatymus' : 'Išskleisti nustatymus'}>
                      {selectedId === section.id ? '▼' : '▶'}
                    </span>
                    <button
                      type="button"
                      className="editor-section-remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeSection(section.id);
                      }}
                      title="Pašalinti"
                    >
                      ×
                    </button>
                  </div>
                  {selectedId === section.id && (
                    <div className="editor-section-props-inline">
                      <h4>Savybės ({LOCALES.find((l) => l.code === activeLang)?.label})</h4>
                      <SectionProps
                        section={section}
                        sections={sections}
                        activeLang={activeLang}
                        onChange={(patch) => updateSection(section.id, patch)}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </main>

        <aside className="editor-panel editor-panel-right">
          <p className="editor-panel-hint">Spustelėkite bloką sąraše – jo nustatymai atsidarys po bloku.</p>
        </aside>
      </div>
      )}
    </div>
  );
}

function SectionPreview({ section, activeLang = 'lt' }) {
  const { type, settings } = section;
  const lang = activeLang || 'lt';
  const getVal = (key) => settings[`${key}_${lang}`] ?? settings[`${key}_lt`] ?? settings[key] ?? '';
  switch (type) {
    case 'hero':
      return (
        <div className="preview-hero">
          <strong>Hero</strong>
          <span>{getVal('title') || '(antraštė)'}</span>
        </div>
      );
    case 'heading':
      return (
        <div className="preview-heading">
          <strong>Antraštė</strong>
          <span>{getVal('text') || '(tekstas)'}</span>
        </div>
      );
    case 'text':
      return (
        <div className="preview-text">
          <strong>Tekstas</strong>
          <span>{(getVal('content') || '').replace(/<[^>]+>/g, ' ').slice(0, 60).trim()}{(getVal('content') || '').length > 60 ? '…' : ''}</span>
        </div>
      );
    case 'image':
      return (
        <div className="preview-image">
          <strong>Nuotrauka</strong>
          <span>{settings.src ? '🖼 Nuotrauka' : '(nuotrauka)'}</span>
        </div>
      );
    case 'button':
      return (
        <div className="preview-button">
          <strong>Mygtukas</strong>
          <span>{getVal('text') || '(mygtukas)'}</span>
        </div>
      );
    case 'toggle':
      return (
        <div className="preview-toggle">
          <strong>Išskleidžiamas blokas</strong>
          <span>{getVal('title') || '(antraštė)'}</span>
        </div>
      );
    case 'spacer':
      return (
        <div className="preview-spacer">
          <strong>Tarpas</strong>
          <span>{settings.height ?? 40}px</span>
        </div>
      );
    case 'products_grid':
      return (
        <div className="preview-products">
          <strong>Prekių tinklelis</strong>
          <span>{getVal('title') || '(iki ' + (settings.limit ?? 8) + ' prekių)'}</span>
        </div>
      );
    case 'feature':
      return (
        <div className="preview-feature">
          <strong>Sekcija (tekstas + nuotrauka)</strong>
          <span>{getVal('title') || '(antraštė)'}</span>
        </div>
      );
    case 'elite_section':
      return (
        <div className="preview-elite">
          <strong>Become the elite</strong>
          <span>{getVal('title') || '(antraštė)'}</span>
        </div>
      );
    default:
      return <span>{type}</span>;
  }
}

function SectionProps({ section, sections, activeLang = 'lt', onChange }) {
  const { type, settings, rowGroup } = section;
  const lang = activeLang || 'lt';
  const getVal = (key) => settings[`${key}_${lang}`] ?? settings[key] ?? '';
  const setVal = (key, value) => onChange({ settings: { [`${key}_${lang}`]: value } });
  const update = (key, value) => onChange({ settings: { [key]: value } });
  const existingRowGroups = [...new Set((sections || []).map((s) => s.rowGroup).filter(Boolean))];
  const rowGroupOptions = [
    { value: '', label: 'Pilnas plotis (viena eilutė)' },
    { value: '__new__', label: 'Nauja eilutė' },
    ...existingRowGroups.map((rg, i) => ({ value: rg, label: `Eilutė ${i + 1}` })),
  ];
  const setRowGroup = (v) => {
    if (v === '__new__') onChange({ rowGroup: generateId() });
    else onChange({ rowGroup: v || null });
  };
  const textAlignOptions = [
    { value: 'left', label: 'Kairė' },
    { value: 'center', label: 'Centras' },
    { value: 'right', label: 'Dešinė' },
    { value: 'justify', label: 'Pagal abu krastus' },
  ];
  const renderRowGroupSelect = () => (
    <div className="section-props-field">
      <label>Blokų eilutė</label>
      <select
        value={rowGroup || ''}
        onChange={(e) => setRowGroup(e.target.value)}
      >
        {rowGroupOptions.map((opt) => (
          <option key={opt.value || 'full'} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      <span className="section-props-hint">Keli blokai su ta pačia eilute rodomi vienoje eilutėje vienas šalia kito.</span>
    </div>
  );
  const renderTextAlignSelect = () => (
    <div className="section-props-field">
      <label>Teksto lygiavimas (horizontalus)</label>
      <select
        value={settings.textAlign || 'left'}
        onChange={(e) => update('textAlign', e.target.value)}
      >
        {textAlignOptions.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );
  const verticalAlignOptions = [
    { value: 'top', label: 'Viršus' },
    { value: 'center', label: 'Centras' },
    { value: 'bottom', label: 'Apačia' },
  ];
  const renderVerticalAlignSelect = () => (
    <div className="section-props-field">
      <label>Vertikalus lygiavimas</label>
      <select
        value={settings.verticalAlign || 'center'}
        onChange={(e) => update('verticalAlign', e.target.value)}
      >
        {verticalAlignOptions.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );

  if (type === 'hero') {
    return (
      <div className="section-props">
        {renderRowGroupSelect()}
        {renderTextAlignSelect()}
        {renderVerticalAlignSelect()}
        <label>Antraštė</label>
        <input
          type="text"
          value={getVal('title')}
          onChange={(e) => setVal('title', e.target.value)}
          placeholder="Hero antraštė"
        />
        <label>Paantraštė</label>
        <input
          type="text"
          value={getVal('subtitle')}
          onChange={(e) => setVal('subtitle', e.target.value)}
          placeholder="Paantraštė"
        />
        <ImageUploadField
          label="Fono nuotrauka"
          value={settings.backgroundImage || ''}
          onChange={(v) => update('backgroundImage', v)}
          placeholder="/uploads/... arba URL"
        />
        <label>Fono vaizdas (video URL)</label>
        <input
          type="text"
          value={settings.backgroundVideo || ''}
          onChange={(e) => update('backgroundVideo', e.target.value)}
          placeholder="https://... .mp4 arba .webm (jei įvesta – naudojama vietoj nuotraukos)"
        />
        <label>Mygtuko tekstas</label>
        <input
          type="text"
          value={getVal('buttonText')}
          onChange={(e) => setVal('buttonText', e.target.value)}
          placeholder="Peržiūrėti"
        />
        <label>Mygtuko nuoroda</label>
        <input
          type="text"
          value={settings.buttonLink || ''}
          onChange={(e) => update('buttonLink', e.target.value)}
          placeholder="/products"
        />
      </div>
    );
  }
  if (type === 'heading') {
    return (
      <div className="section-props">
        {renderRowGroupSelect()}
        {renderTextAlignSelect()}
        <label>Lygis</label>
        <select
          value={settings.tag || 'h2'}
          onChange={(e) => update('tag', e.target.value)}
        >
          <option value="h1">H1</option>
          <option value="h2">H2</option>
          <option value="h3">H3</option>
        </select>
        <label>Tekstas</label>
        <input
          type="text"
          value={getVal('text')}
          onChange={(e) => setVal('text', e.target.value)}
          placeholder="Antraštės tekstas"
        />
      </div>
    );
  }
  if (type === 'text') {
    return (
      <div className="section-props">
        {renderRowGroupSelect()}
        {renderTextAlignSelect()}
        <label>Tekstas</label>
        <RichTextEditor
          value={getVal('content')}
          onChange={(html) => setVal('content', html)}
          placeholder="Pastraipa..."
          minHeight={120}
        />
      </div>
    );
  }
  if (type === 'image') {
    return (
      <div className="section-props">
        {renderRowGroupSelect()}
        {renderTextAlignSelect()}
        <ImageUploadField
          label="Nuotrauka"
          value={settings.src || ''}
          onChange={(v) => update('src', v)}
          placeholder="/uploads/..."
        />
        <label>Alt tekstas</label>
        <input
          type="text"
          value={getVal('alt')}
          onChange={(e) => setVal('alt', e.target.value)}
          placeholder="Aprašymas"
        />
        <label>Nuoroda (pasirinktinai)</label>
        <input
          type="text"
          value={settings.link || ''}
          onChange={(e) => update('link', e.target.value)}
          placeholder="/products"
        />
      </div>
    );
  }
  if (type === 'button') {
    return (
      <div className="section-props">
        {renderRowGroupSelect()}
        {renderTextAlignSelect()}
        <label>Tekstas</label>
        <input
          type="text"
          value={getVal('text')}
          onChange={(e) => setVal('text', e.target.value)}
          placeholder="Mygtuko tekstas"
        />
        <label>Nuoroda</label>
        <input
          type="text"
          value={settings.link || ''}
          onChange={(e) => update('link', e.target.value)}
          placeholder="/products"
        />
      </div>
    );
  }
  if (type === 'toggle') {
    return (
      <div className="section-props">
        {renderRowGroupSelect()}
        <label>Antraštė (summary)</label>
        <input
          type="text"
          value={getVal('title')}
          onChange={(e) => setVal('title', e.target.value)}
          placeholder="Specifikacija"
        />
        <label>Turinys (išskleidžiamas)</label>
        <RichTextEditor
          value={getVal('content')}
          onChange={(html) => setVal('content', html)}
          placeholder="Tekstas arba HTML..."
          minHeight={120}
        />
        <label className="toggle-label">
          <input
            type="checkbox"
            checked={!!settings.openByDefault}
            onChange={(e) => update('openByDefault', e.target.checked)}
            className="input-checkbox"
          />
          <span>Išskleista pagal numatymą</span>
        </label>
      </div>
    );
  }
  if (type === 'spacer') {
    return (
      <div className="section-props">
        {renderRowGroupSelect()}
        <label>Aukštis (px)</label>
        <input
          type="number"
          min={8}
          max={200}
          value={settings.height ?? 40}
          onChange={(e) => update('height', parseInt(e.target.value, 10) || 40)}
        />
      </div>
    );
  }
  if (type === 'products_grid') {
    return (
      <div className="section-props">
        {renderRowGroupSelect()}
        <label>Antraštė</label>
        <input
          type="text"
          value={getVal('title')}
          onChange={(e) => setVal('title', e.target.value)}
          placeholder="Prekės"
        />
        <label>Prekių skaičius</label>
        <input
          type="number"
          min={1}
          max={24}
          value={settings.limit ?? 8}
          onChange={(e) => update('limit', parseInt(e.target.value, 10) || 8)}
        />
      </div>
    );
  }
  if (type === 'feature') {
    return (
      <div className="section-props">
        {renderRowGroupSelect()}
        <label>Antraštė</label>
        <input
          type="text"
          value={getVal('title')}
          onChange={(e) => setVal('title', e.target.value)}
          placeholder="UnmatchedStability"
        />
        <label>Tekstas</label>
        <RichTextEditor
          value={getVal('text')}
          onChange={(html) => setVal('text', html)}
          placeholder="Sekcijos tekstas..."
          minHeight={100}
        />
        <ImageUploadField
          label="Nuotrauka"
          value={settings.imageSrc || ''}
          onChange={(v) => update('imageSrc', v)}
          placeholder="/uploads/..."
        />
        <label>Alt tekstas</label>
        <input
          type="text"
          value={settings.imageAlt || ''}
          onChange={(e) => update('imageAlt', e.target.value)}
          placeholder="Aprašymas"
        />
        <label>Tekstas kairėje / nuotrauka dešinėje</label>
        <select
          value={settings.align || 'left'}
          onChange={(e) => update('align', e.target.value)}
        >
          <option value="left">Taip (tekstas kairėje)</option>
          <option value="right">Ne (tekstas dešinėje)</option>
        </select>
      </div>
    );
  }
  if (type === 'elite_section') {
    return (
      <div className="section-props">
        {renderRowGroupSelect()}
        <label>Antraštė</label>
        <input
          type="text"
          value={getVal('title')}
          onChange={(e) => setVal('title', e.target.value)}
          placeholder="Become the elite"
        />
        <label>Tekstas</label>
        <RichTextEditor
          value={getVal('text')}
          onChange={(html) => setVal('text', html)}
          placeholder="Sekcijos tekstas..."
          minHeight={100}
        />
        <label>Mygtuko tekstas</label>
        <input
          type="text"
          value={getVal('buttonText')}
          onChange={(e) => setVal('buttonText', e.target.value)}
          placeholder="Discover the Science"
        />
        <label>Mygtuko nuoroda</label>
        <input
          type="text"
          value={settings.buttonLink || ''}
          onChange={(e) => update('buttonLink', e.target.value)}
          placeholder="/products"
        />
        <ImageUploadField
          label="Vaizdas 1"
          value={settings.image1 || ''}
          onChange={(v) => update('image1', v)}
          placeholder="/uploads/..."
        />
        <ImageUploadField
          label="Vaizdas 2"
          value={settings.image2 || ''}
          onChange={(v) => update('image2', v)}
          placeholder="/uploads/..."
        />
        <ImageUploadField
          label="Vaizdas 3"
          value={settings.image3 || ''}
          onChange={(v) => update('image3', v)}
          placeholder="/uploads/..."
        />
      </div>
    );
  }
  return null;
}

export { BLOCK_TYPES, DEFAULT_SETTINGS, generateId, SectionPreview, SectionProps, ImageUploadField };
export default AdminHomePageEditor;
